<template>
    <div class="container admin-table-page">
        <!-- HEADER -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="d-flex align-items-center">
                    <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                        <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
                    </b-button>
                    <h2 class="param-title">{{ labels.title }}</h2>
                </div>
            </div>
        </div>

        <!-- FILTROS PRINCIPALES -->
        <div class="param-title-wrap niif-header" v-if="showFilters">
            <div class="d-flex align-items-center w-100">
                <div class="filters-container d-flex align-items-center">
                    <b-form-select
                        v-model="codReporte"
                        @change="f_actualiza_reporte()"
                        class="filter-select"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectReport }}
                        </b-form-select-option>
                        <option v-for="reporte in reportes" :key="reporte.codigo" :value="reporte.codigo">
                            {{ reporte?.codigo }} - {{ reporte?.nombre }}
                        </option>
                    </b-form-select>

                    <b-form-select
                        v-model="selVigencia"
                        @change="f_actualiza_vigencia()"
                        class="filter-select ml-2"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectValidity }}
                        </b-form-select-option>
                        <option v-for="vigencia in listVigencia" :key="vigencia.vigencia" :value="vigencia.vigencia">
                            {{ vigencia?.vigencia }}
                        </option>
                    </b-form-select>

                    <b-form-select
                        v-model="grupoId"
                        @change="f_filterCompany"
                        class="filter-select ml-2"
                    >
                        <b-form-select-option value="">
                            {{ labels.allGroups }}
                        </b-form-select-option>
                        <option v-for="grupo in listGrupos" :key="grupo.id" :value="grupo.id">
                            {{ grupo?.nombre }}
                        </option>
                    </b-form-select>

                    <b-form-select
                        v-model="companyId"
                        @change="f_actualiza_compania()"
                        class="filter-select ml-2"
                        :disabled="filteredCompanies.length === 0"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectCompany }}
                        </b-form-select-option>
                        <option v-for="company in filteredCompanies" :key="company.id" :value="String(company.id)">
                            {{ company?.acronym }} {{ company?.nombre }}
                        </option>
                    </b-form-select>

                    <b-button
                        class="consult-btn ml-2"
                        @click="consultaNiveles"
                        :disabled="!isFormValid || isLoading"
                    >
                        <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
                        {{ labels.consultar }}
                    </b-button>

                    <b-button
                        variant="link"
                        class="toggle-filters-btn ml-2"
                        @click="toggleFilters"
                        v-if="showNiveles"
                    >
                        <font-awesome-icon :icon="['fas', 'chevron-up']" class="mr-1" />
                        {{ labels.hideFilters }}
                    </b-button>
                </div>
            </div>
        </div>

        <!-- FILTROS DE NIVELES -->
        <div class="param-title-wrap niif-header" v-if="showNiveles && !showFilters">
            <div class="d-flex align-items-center w-100">
                <div class="filters-container d-flex align-items-center flex-wrap">
                    <b-button 
                        variant="link" 
                        class="toggle-filters-btn mr-2"
                        @click="toggleFilters"
                    >
                        <font-awesome-icon :icon="['fas', 'chevron-down']" class="mr-1" />
                        {{ labels.showFilters }}
                    </b-button>

                    <b-form-select
                        v-model="filtroNivel1"
                        @change="onNivel1Change"
                        class="filter-select nivel-filter"
                        :disabled="level1List.length === 0"
                    >
                        <b-form-select-option value="">
                            {{ labels.level1 }}
                        </b-form-select-option>
                        <option v-for="nivel in level1List" :key="nivel.id" :value="nivel.nivel_1">
                            {{ nivel.nivel_1 }} - {{ nivel.descripcion }}
                        </option>
                    </b-form-select>

                    <b-form-select
                        v-model="filtroNivel2"
                        @change="onNivel2Change"
                        class="filter-select nivel-filter ml-2"
                        :disabled="level2List.length === 0 || !filtroNivel1"
                        v-if="showDos"
                    >
                        <b-form-select-option value="">
                            {{ labels.level2 }}
                        </b-form-select-option>
                        <option v-for="nivel in level2List" :key="nivel.id" :value="nivel.nivel_2">
                            {{ nivel.nivel_2 }} - {{ nivel.descripcion }}
                        </option>
                    </b-form-select>

                    <b-form-select
                        v-model="filtroNivel3"
                        @change="onNivel3Change"
                        class="filter-select nivel-filter ml-2"
                        :disabled="level3List.length === 0 && !hasNullNivel3 || filtroNivel3 === 'null'"
                        v-if="showTres"
                    >
                        <b-form-select-option value="">
                            {{ labels.level3 }}
                        </b-form-select-option>
                        <b-form-select-option value="null" v-if="hasNullNivel3">
                            {{ labels.level3Null }}
                        </b-form-select-option>
                        <option v-for="nivel in level3ListFiltrados" :key="nivel.id" :value="nivel.nivel_3">
                            {{ nivel.nivel_3 }} - {{ nivel.descripcion }}
                        </option>
                    </b-form-select>

                    <b-form-select
                        v-model="filtroNivel4"
                        @change="onNivel4Change"
                        class="filter-select nivel-filter ml-2"
                        :disabled="level4List.length === 0"
                        v-if="showCuatro"
                    >
                        <b-form-select-option value="">
                            {{ labels.level4 }}
                        </b-form-select-option>
                        <option v-for="nivel in level4List" :key="nivel.id" :value="nivel.nivel_4">
                            {{ nivel.nivel_4 }} - {{ nivel.descripcion }}
                        </option>
                    </b-form-select>
                </div>
            </div>
        </div>

        <!-- CONTENIDO PRINCIPAL -->
        <div class="admin-table-block mt-4">
            <!-- Spinner de carga -->
            <div v-if="isLoading" class="text-center py-5">
                <b-spinner></b-spinner>
                <p class="mt-2">{{ labels.loading }}</p>
            </div>

            <!-- ESTADO INICIAL - Sin datos -->
            <div v-else-if="!consultaRealizada && !showNiveles && level1List.length === 0">
                <CorporateDataTable
                    :items="[]"
                    :fields="[]"
                    :labels="detailTableLabels"
                    :loading="false"
                    :default-per-page="10"
                    :page-options="[10]"
                    :narrow="false"
                    :table-min-height="490"
                    :table-max-height="500"
                    :show-search="false"
                    :allow-column-toggle="false"
                    :enable-sorting="false"
                    :show-pagination="false"
                    :show-rows-per-page="false"
                >
                    <template #empty-state>
                        <div class="text-center py-5">
                            <font-awesome-icon
                                :icon="['fas', 'search']"
                                size="3x"
                                class="text-muted mb-3"
                                style="opacity: 0.3;"
                            />
                            <p class="h4 text-muted">
                                {{ labels.selectFilters }}
                            </p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- TABLA DE DETALLE (Nivel 4 seleccionado) -->
            <div v-else-if="hasNivel4Selected">
                <CorporateDataTable
                    :items="detalleNivCuatro"
                    :fields="detailFields"
                    :labels="detailTableLabels"
                    :default-per-page="10"
                    :page-options="[10, 20, 50, 100]"
                    :narrow="false"
                    :table-min-height="490"
                    :table-max-height="500"
                    :show-search="true"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="detailFilterDefs"
                    :toolbar-disabled="!hasNivel4Selected || detalleNivCuatro.length === 0"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-add-btn"
                                @click="openCreateCuentaModal"
                                :disabled="!hasNivel4Selected || isVigenciaBloqueada || !consultaRealizada"
                            >
                                {{ labels.addAccount }}
                            </b-button>
                            <b-button
                                size="sm"
                                class="reset-btn-danger ml-1"
                                style="height: 31px; min-height: 31px; padding: 0 14px; display: inline-flex; align-items: center; justify-content: center; white-space: nowrap; border-radius: 7px; font-size: 13px;"
                                @click="borrarTodosDetalles"
                                :disabled="!hasNivel4Selected || detalleNivCuatro.length === 0 || isVigenciaBloqueada || !consultaRealizada"
                            >
                                <font-awesome-icon :icon="['fas', 'trash']" class="mr-1" />
                                {{ labels.deleteAll }}
                            </b-button>
                        </div>
                    </template>

                    <template #cell(contable)="{ item }">
                        <div class="text-center">{{ item.contable == 1 ? 'SI' : 'NO' }}</div>
                    </template>

                    <template #cell(cuenta)="{ item }">
                        <div class="text-left">{{ item.cuenta || '-' }}</div>
                    </template>

                    <template #cell(actual)="{ item }">
                        <div class="text-center">{{ item.actual == 1 ? 'SI' : 'NO' }}</div>
                    </template>

                    <template #cell(operacion)="{ item }">
                        <div class="text-left">{{ item.operacion || '-' }}</div>
                    </template>

                    <template #cell(formulacion)="{ item }">
                        <div class="text-left">{{ item.contable == 1 ? '' : (item.formulacion || '-') }}</div>
                    </template>

                    <template #cell(accionEdit)="{ item }">
                        <b-button
                            style="background-color: #FF924C; width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            class="md-fab md-primary"
                            @click.stop="editCuenta(item.id)"
                            :disabled="isVigenciaBloqueada || !consultaRealizada"
                        >
                            <font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs"/>
                        </b-button>
                    </template>

                    <template #cell(accionDelete)="{ item }">
                        <b-button
                            class="reset-btn-danger"
                            style="width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            @click.stop="deleteCuenta(item.id)"
                            :disabled="isVigenciaBloqueada || !consultaRealizada"
                        >
                            <font-awesome-icon :icon="['fas', 'trash']" size="xs"/>
                        </b-button>
                    </template>

                    <template #empty>
                        <div class="text-center py-4">
                            <font-awesome-icon :icon="['fas', 'table']" class="empty-table-icon" />
                            <p class="text-muted mt-2">{{ labels.noAccounts }}</p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- Tabla para Nivel 1 -->
            <div v-else-if="mostrarNivel1Table && level1List.length > 0">
                <CorporateDataTable
                    :items="level1TableData"
                    :fields="nivel1Fields"
                    :labels="nivel1TableLabels"
                    :default-per-page="10"
                    :page-options="[10, 20, 50, 100]"
                    :narrow="false"
                    :table-min-height="490"
                    :table-max-height="500"
                    :show-search="true"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="nivel1FilterDefs"
                    :toolbar-disabled="false"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-add-btn"
                                @click="openCreateNivel1"
                                :disabled="isVigenciaBloqueada || !consultaRealizada"
                            >
                                {{ labels.createLevel1 }}
                            </b-button>
                            <b-button
                                size="sm"
                                class="reset-btn-danger ml-1"
                                style="height: 31px; min-height: 31px; padding: 0 14px; display: inline-flex; align-items: center; justify-content: center; white-space: nowrap; border-radius: 7px; font-size: 13px;"
                                @click="borrarTodosNiveles1"
                                :disabled="level1TableData.length === 0 || isVigenciaBloqueada || !consultaRealizada"
                            >
                                <font-awesome-icon :icon="['fas', 'trash']" class="mr-1" />
                                {{ labels.deleteAll }}
                            </b-button>
                        </div>
                    </template>

                    <template #cell(accionEdit)="{ item }">
                        <b-button
                            style="background-color: #FF924C; width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            class="md-fab md-primary"
                            @click.stop="editNivel1(item)"
                            :disabled="isVigenciaBloqueada || !consultaRealizada"
                        >
                            <font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs"/>
                        </b-button>
                    </template>

                    <template #cell(accionDelete)="{ item }">
                        <b-button
                            class="reset-btn-danger"
                            style="width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            @click.stop="deleteNivel1(item)"
                            :disabled="isVigenciaBloqueada || !consultaRealizada"
                        >
                            <font-awesome-icon :icon="['fas', 'trash']" size="xs"/>
                        </b-button>
                    </template>

                    <template #empty>
                        <div class="text-center py-4">
                            <font-awesome-icon :icon="['fas', 'table']" class="empty-table-icon" />
                            <p class="text-muted mt-2">{{ labels.noLevel1Data }}</p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- Tabla para Nivel 2 -->
            <div v-else-if="mostrarNivel2Table && level2List.length > 0">
                <CorporateDataTable
                    :items="level2TableData"
                    :fields="nivel2Fields"
                    :labels="nivel2TableLabels"
                    :default-per-page="10"
                    :page-options="[10, 20, 50, 100]"
                    :narrow="false"
                    :table-min-height="490"
                    :table-max-height="500"
                    :show-search="true"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="nivel2FilterDefs"
                    :toolbar-disabled="false"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-add-btn"
                                @click="openCreateNivel2"
                                :disabled="isVigenciaBloqueada || !consultaRealizada"
                            >
                                {{ labels.createLevel2 }}
                            </b-button>
                            <b-button
                                size="sm"
                                class="reset-btn-danger ml-1"
                                style="height: 31px; min-height: 31px; padding: 0 14px; display: inline-flex; align-items: center; justify-content: center; white-space: nowrap; border-radius: 7px; font-size: 13px;"
                                @click="borrarTodosNiveles2"
                                :disabled="level2TableData.length === 0 || isVigenciaBloqueada || !consultaRealizada"
                            >
                                <font-awesome-icon :icon="['fas', 'trash']" class="mr-1" />
                                {{ labels.deleteAll }}
                            </b-button>
                        </div>
                    </template>

                    <template #cell(perteneceA)="{ item }">
                        <div class="text-left">{{ item.perteneceA || '-' }}</div>
                    </template>

                    <template #cell(accionEdit)="{ item }">
                        <b-button
                            style="background-color: #FF924C; width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            class="md-fab md-primary"
                            @click.stop="editNivel2(item)"
                            :disabled="isVigenciaBloqueada || !consultaRealizada"
                        >
                            <font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs"/>
                        </b-button>
                    </template>

                    <template #cell(accionDelete)="{ item }">
                        <b-button
                            class="reset-btn-danger"
                            style="width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            @click.stop="deleteNivel2(item)"
                            :disabled="isVigenciaBloqueada || !consultaRealizada"
                        >
                            <font-awesome-icon :icon="['fas', 'trash']" size="xs"/>
                        </b-button>
                    </template>

                    <template #empty>
                        <div class="text-center py-4">
                            <font-awesome-icon :icon="['fas', 'table']" class="empty-table-icon" />
                            <p class="text-muted mt-2">{{ labels.noLevel2Data }}</p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- Tabla para Nivel 3 -->
            <div v-else-if="mostrarNivel3Table && level3ListFiltrados.length > 0">
                <CorporateDataTable
                    :items="level3TableData"
                    :fields="nivel3Fields"
                    :labels="nivel3TableLabels"
                    :default-per-page="10"
                    :page-options="[10, 20, 50, 100]"
                    :narrow="false"
                    :table-min-height="490"
                    :table-max-height="500"
                    :show-search="true"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="nivel3FilterDefs"
                    :toolbar-disabled="false"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-add-btn"
                                @click="openCreateNivel3"
                                :disabled="isVigenciaBloqueada || !consultaRealizada"
                            >
                                {{ labels.createLevel3 }}
                            </b-button>
                            <b-button
                                size="sm"
                                class="reset-btn-danger ml-1"
                                style="height: 31px; min-height: 31px; padding: 0 14px; display: inline-flex; align-items: center; justify-content: center; white-space: nowrap; border-radius: 7px; font-size: 13px;"
                                @click="borrarTodosNiveles3"
                                :disabled="level3TableData.length === 0 || isVigenciaBloqueada || !consultaRealizada"
                            >
                                <font-awesome-icon :icon="['fas', 'trash']" class="mr-1" />
                                {{ labels.deleteAll }}
                            </b-button>
                        </div>
                    </template>

                    <template #cell(perteneceA)="{ item }">
                        <div class="text-left">{{ item.perteneceA || '-' }}</div>
                    </template>

                    <template #cell(accionEdit)="{ item }">
                        <b-button
                            style="background-color: #FF924C; width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            class="md-fab md-primary"
                            @click.stop="editNivel3(item)"
                            :disabled="isVigenciaBloqueada || !consultaRealizada"
                        >
                            <font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs"/>
                        </b-button>
                    </template>

                    <template #cell(accionDelete)="{ item }">
                        <b-button
                            class="reset-btn-danger"
                            style="width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            @click.stop="deleteNivel3(item)"
                            :disabled="isVigenciaBloqueada || !consultaRealizada"
                        >
                            <font-awesome-icon :icon="['fas', 'trash']" size="xs"/>
                        </b-button>
                    </template>

                    <template #empty>
                        <div class="text-center py-4">
                            <font-awesome-icon :icon="['fas', 'table']" class="empty-table-icon" />
                            <p class="text-muted mt-2">{{ labels.noLevel3Data }}</p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- Tabla para Nivel 4 -->
            <div v-else-if="mostrarNivel4Table && level4List.length > 0">
                <CorporateDataTable
                    :items="level4TableData"
                    :fields="nivel4Fields"
                    :labels="nivel4TableLabels"
                    :default-per-page="10"
                    :page-options="[10, 20, 50, 100]"
                    :narrow="false"
                    :table-min-height="490"
                    :table-max-height="500"
                    :show-search="true"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="nivel4FilterDefs"
                    :toolbar-disabled="false"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-add-btn"
                                @click="openCreateNivel4"
                                :disabled="isVigenciaBloqueada || !consultaRealizada"
                            >
                                {{ labels.createLevel4 }}
                            </b-button>
                            <b-button
                                size="sm"
                                class="reset-btn-danger ml-1"
                                style="height: 31px; min-height: 31px; padding: 0 14px; display: inline-flex; align-items: center; justify-content: center; white-space: nowrap; border-radius: 7px; font-size: 13px;"
                                @click="borrarTodosNiveles4"
                                :disabled="level4TableData.length === 0 || isVigenciaBloqueada || !consultaRealizada"
                            >
                                <font-awesome-icon :icon="['fas', 'trash']" class="mr-1" />
                                {{ labels.deleteAll }}
                            </b-button>
                        </div>
                    </template>

                    <template #cell(perteneceA)="{ item }">
                        <div class="text-left">{{ item.perteneceA || '-' }}</div>
                    </template>

                    <template #cell(accionEdit)="{ item }">
                        <b-button
                            style="background-color: #FF924C; width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            class="md-fab md-primary"
                            @click.stop="editNivel4(item)"
                            :disabled="isVigenciaBloqueada || !consultaRealizada"
                        >
                            <font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs"/>
                        </b-button>
                    </template>

                    <template #cell(accionDelete)="{ item }">
                        <b-button
                            class="reset-btn-danger"
                            style="width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            @click.stop="deleteNivel4(item)"
                            :disabled="isVigenciaBloqueada || !consultaRealizada"
                        >
                            <font-awesome-icon :icon="['fas', 'trash']" size="xs"/>
                        </b-button>
                    </template>

                    <template #empty>
                        <div class="text-center py-4">
                            <font-awesome-icon :icon="['fas', 'table']" class="empty-table-icon" />
                            <p class="text-muted mt-2">{{ labels.noLevel4Data }}</p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- ESTADO VACÍO - Consulta realizada sin datos -->
            <div v-else-if="consultaRealizada && 
                level1List.length === 0 && 
                level2List.length === 0 && 
                level3List.length === 0 && 
                level4List.length === 0 && 
                detalleNivCuatro.length === 0"
                class="text-center py-5"
            >
                <font-awesome-icon
                    :icon="['fas', 'table']"
                    size="3x"
                    class="text-muted mb-3"
                    style="opacity: 0.3;"
                />
                <p class="h4 text-muted">
                    {{ labels.noData }}
                </p>
                <p class="text-muted">
                    {{ labels.noResults }}
                </p>
            </div>

            <!-- ESTADO DE SELECCIÓN DE NIVELES (Nivel 4 no seleccionado) -->
            <div v-else-if="!hasNivel4Selected && (mostrarNivel1Table || mostrarNivel2Table || mostrarNivel3Table || mostrarNivel4Table)" 
                class="text-center py-5"
            >
                <font-awesome-icon
                    :icon="['fas', 'layers']"
                    size="3x"
                    class="text-muted mb-3"
                    style="opacity: 0.3;"
                />
                <p class="h4 text-muted">
                    {{ labels.selectAllLevels }}
                </p>
            </div>
        </div>

        <!-- MODALES -->
        <CreaNivelUno
            :showCreaNivUno="showCreaNivUno"
            :codReporte="codReporte"
            @closeModal="showCreaNivUno = false"
            @refresNivelUno="onRefresNivelUno"
        />

        <CreaNivelDos
            :showCreaNivDos="showCreaNivDos"
            :codReporte="codReporte"
            :nivelUnoSelect="selectedLevel1Data ? `${selectedLevel1Data.nivel_1} - ${selectedLevel1Data.descripcion}` : ''"
            @closeModal="showCreaNivDos = false"
            @refresNivelDos="onRefresNivelDos"
        />

        <CreaNivelTres
            :showCreaNivTres="showCreaNivTres"
            :codReporte="codReporte"
            :nivelUnoSelect="selectedLevel1Data ? `${selectedLevel1Data.nivel_1} - ${selectedLevel1Data.descripcion}` : ''"
            :nivelDosSelect="selectedLevel2Data ? `${selectedLevel2Data.nivel_2} - ${selectedLevel2Data.descripcion}` : ''"
            @closeModal="showCreaNivTres = false"
            @refresNivelTres="onRefresNivelTres"
        />

        <CreaNivelCuatro
            :showCreaNivCuatro="showCreaNivCuatro"
            :codReporte="codReporte"
            :nivelUnoSelect="selectedLevel1Data ? `${selectedLevel1Data.nivel_1} - ${selectedLevel1Data.descripcion}` : ''"
            :nivelDosSelect="selectedLevel2Data ? `${selectedLevel2Data.nivel_2} - ${selectedLevel2Data.descripcion}` : ''"
            :nivelTresSelect="selectedLevel3Data ? `${selectedLevel3Data.nivel_3} - ${selectedLevel3Data.descripcion}` : ''"
            @closeModal="showCreaNivCuatro = false"
            @refresNivelCuatro="onRefresNivelCuatro"
        />

        <CreaCuenta
            :modalCreaCuenta="showCreaCuenta"
            :nivelSelected="nivelSelect"
            :vigComSelected="dataVigenCompa"
            :cuentasCreadas="cuentasCreadasList"
            @clicked="closeCreaCuentaModal"
            @reloadTable="reloadTableAfterCreate"
        />

        <EditaCuenta
            :modalEditaCuenta="showEditaCuenta"
            :dataDetallCuent="cuentaSeleccionada"
            :vigComSelected="dataVigenCompa"
            @clicked="closeEditaCuentaModal"
            @reloadTable="reloadTableAfterEdit"
        />

        <!-- MODAL CONFIRMACIÓN ELIMINAR -->
        <b-modal
            :model-value="showConfirmDeleteModal"
            :no-close-on-backdrop="true"
            hide-footer
                        centered
            size="md"
            :title="labels.confirmDelete"
            header-class="reset-modal-header"
            title-class="reset-modal-title"
            body-class="reset-modal-body"
         @hide="closeConfirmDeleteModal">
            <div class="uniform-modal-body">
                <div class="password-policy-box mb-4">
                    <div class="password-policy-title">{{ labels.areYouSure }}</div>
                    <div class="password-policy-subtitle">{{ labels.confirmDeleteText }}</div>
                </div>
                <div class="reset-dialog-actions d-flex justify-content-end mt-4">
                    <b-button class="reset-btn-secondary" @click="closeConfirmDeleteModal">
                        {{ labels.cancel }}
                    </b-button>
                    <b-button class="reset-btn-primary ml-2" @click="executeDelete" style="background-color: #cd2838; border-color: #cd2838;">
                        {{ labels.confirm }}
                    </b-button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import Nivel from '../../services/Nivel'
import PlanCuentas from "../../services/PlanCuentas"
import Company from "../../services/Company"
import Balance from "../../services/BalanceNiif"
import Grupo from "../../services/Grupo"
import CorporateDataTable from "../utils/CorporateDataTable.vue"
import CreaNivelUno from "./CreaNivelUno.vue"
import CreaNivelDos from "./CreaNivelDos.vue"
import CreaNivelTres from "./CreaNivelTres.vue"
import CreaNivelCuatro from "./CreaNivelCuatro.vue"
import CreaCuenta from "./CreaCuenta.vue"
import EditaCuenta from "./EditaCuenta.vue" 

export default {
    name: "Niveles",
    components: {
        CorporateDataTable,
        CreaNivelUno,
        CreaNivelDos,
        CreaNivelTres,
        CreaNivelCuatro,
        CreaCuenta,
        EditaCuenta
    },
    data: () => ({
        codReporte: '',
        selVigencia: '',
        companyId: '',
        grupoId: '',
        listVigencia: [],
        listCompany: [],
        listGrupos: [],
        filteredCompanies: [],
        reportes: [],
        companiaUsuario: [],
        showCreaCuenta: false,
        showEditaCuenta: false,
        cuentasCreadasList: [],
        cuentaSeleccionada: null,
        
        isLoading: false,
        showNiveles: false,
        showFilters: true,
        consultaRealizada: false,
        
        vigencia_anterior: '',
        compania_anterior: '',
        codReporteAnterior: '',
        
        showDos: false,
        showTres: false,
        showCuatro: false,
        
        level1List: [],
        level2List: [],
        level3List: [],
        level4List: [],
        
        filtroNivel1: '',
        filtroNivel2: '',
        filtroNivel3: '',
        filtroNivel4: '',
        
        hasNullNivel3: false,
        
        detalleNivCuatro: [],
        nivelSelect: {},
        dataVigenCompa: {},
        
        showCreaNivUno: false,
        showCreaNivDos: false,
        showCreaNivTres: false,
        showCreaNivCuatro: false,
        showConfirmDeleteModal: false,
        deleteId: null,
        isDeleteAll: false,
        deleteTarget: 'cuenta',
        nivelToDelete: null,
        
        createData: {
            selectedLevel: '',
            nivel1: { codigo: '', descripcion: '' },
            nivel2: { parentId: '', codigo: '', descripcion: '' },
            nivel3: { parentId: '', codigo: '', descripcion: '' },
            nivel4: { parentId: '', codigo: '', descripcion: '' }
        }
    }),
    computed: {
        labels() {
            const baseTitle = this.$route.params.tipo == 'directo' 
                ? "Niveles: Flujo De Efectivo - Metodo Directo" 
                : "Niveles: Flujo De Efectivo - Metodo Indirecto"
            
            if (this.$store.state._language == "es-ES") {
                return {
                    title: baseTitle,
                    selectReport: "Seleccione Reporte",
                    selectValidity: "Seleccione Vigencia",
                    selectCompany: "Seleccione Compañía",
                    allGroups: "Todos los grupos",
                    consultar: "Consultar",
                    showFilters: "Mostrar filtros",
                    hideFilters: "Ocultar filtros",
                    create: "Crear",
                    createLevel: "Crear Nivel",
                    createLevel1: "Crear Nivel 1",
                    createLevel2: "Crear Nivel 2",
                    createLevel3: "Crear Nivel 3",
                    createLevel4: "Crear Nivel 4",
                    createAccount: "Agregar Cuenta",
                    createAccountInfo: "Acción restringida: Acceso administrador",
                    selectLevel: "Seleccionar Nivel",
                    selectLevelOption: "Seleccione un nivel...",
                    level1: "Nivel 1",
                    level2: "Nivel 2",
                    level3: "Nivel 3",
                    level3Null: "Nivel 3 (NULL)",
                    level4: "Nivel 4",
                    levelFirst: "Código Nivel 1",
                    levelSecond: "Código Nivel 2",
                    levelThird: "Código Nivel 3",
                    levelFourth: "Código Nivel 4",
                    belongsTo: "Pertenece a",
                    name: "Nombre",
                    save: "Guardar",
                    close: "Cerrar",
                    loading: "Cargando...",
                    nivel: "Nivel",
                    codigo: "Código",
                    descripcion: "Descripción",
                    perteneceA: "Pertenece a",
                    edit: "Editar",
                    delete: "Eliminar",
                    deleteAll: "Eliminar Todo",
                    addAccount: "Agregar Cuenta",
                    noAccounts: "No hay cuentas para este nivel",
                    noLevel1Data: "No hay niveles 1 disponibles",
                    noLevel2Data: "No hay niveles 2 disponibles",
                    noLevel3Data: "No hay niveles 3 disponibles",
                    noLevel4Data: "No hay niveles 4 disponibles",
                    noData: "No hay datos disponibles",
                    noResults: "No se encontraron resultados",
                    selectFilters: "Seleccione los filtros y consulte la información",
                    noMatches: "No se encontraron resultados",
                    searchPlaceholder: "Buscar en la tabla...",
                    rowsPerPage: "Filas por página:",
                    noSort: "Sin orden",
                    sortAsc: "A-Z / Menor a mayor",
                    sortDesc: "Z-A / Mayor a menor",
                    columns: "Columnas",
                    showAllColumns: "Mostrar todas",
                    filters: "Filtros",
                    filterNivel: "Filtrar nivel...",
                    filterCodigo: "Filtrar código...",
                    filterDescripcion: "Filtrar descripción...",
                    filterPertenece: "Filtrar pertenece a...",
                    selectAllLevels: "Seleccione los 4 niveles para ver los datos",
                    contable: "Contable",
                    cuenta: "Cuenta",
                    actual: "Actual",
                    operacion: "Operación",
                    formulacion: "Formulación",
                    confirmDelete: "Confirmar Eliminación",
                    areYouSure: "¿Está seguro?",
                    confirmDeleteText: "¿Está seguro que desea eliminar este registro?",
                    cancel: "Cancelar",
                    confirm: "Confirmar"
                }
            } else {
                return {
                    title: this.$route.params.tipo == 'directo' 
                        ? "Levels: Cash Flow - Direct Method" 
                        : "Levels: Cash Flow - Indirect Method",
                    selectReport: "Select Report",
                    selectValidity: "Select Validity",
                    selectCompany: "Select Company",
                    allGroups: "All groups",
                    consultar: "Consult",
                    showFilters: "Show filters",
                    hideFilters: "Hide filters",
                    create: "Create",
                    createLevel: "Create Level",
                    createLevel1: "Create Level 1",
                    createLevel2: "Create Level 2",
                    createLevel3: "Create Level 3",
                    createLevel4: "Create Level 4",
                    createAccount: "Add Account",
                    createAccountInfo: "Feature in development",
                    selectLevel: "Select Level",
                    selectLevelOption: "Select a level...",
                    level1: "Level 1",
                    level2: "Level 2",
                    level3: "Level 3",
                    level3Null: "Level 3 (NULL)",
                    level4: "Level 4",
                    levelFirst: "Level 1 Code",
                    levelSecond: "Level 2 Code",
                    levelThird: "Level 3 Code",
                    levelFourth: "Level 4 Code",
                    belongsTo: "Belongs to",
                    name: "Name",
                    save: "Save",
                    close: "Close",
                    loading: "Loading...",
                    nivel: "Level",
                    codigo: "Code",
                    descripcion: "Description",
                    perteneceA: "Belongs to",
                    edit: "Edit",
                    delete: "Delete",
                    deleteAll: "Delete All",
                    addAccount: "Add Account",
                    noAccounts: "No accounts for this level",
                    noLevel1Data: "No level 1 data available",
                    noLevel2Data: "No level 2 data available",
                    noLevel3Data: "No level 3 data available",
                    noLevel4Data: "No level 4 data available",
                    noData: "No data available",
                    noResults: "No results found",
                    selectFilters: "Select filters and consult the information",
                    noMatches: "No results found",
                    searchPlaceholder: "Search in table...",
                    rowsPerPage: "Rows per page:",
                    noSort: "No sorting",
                    sortAsc: "A-Z / Low to high",
                    sortDesc: "Z-A / High to low",
                    columns: "Columns",
                    showAllColumns: "Show all",
                    filters: "Filters",
                    filterNivel: "Filter level...",
                    filterCodigo: "Filter code...",
                    filterDescripcion: "Filter description...",
                    filterPertenece: "Filter belongs to...",
                    selectAllLevels: "Select all 4 levels to view data",
                    contable: "Accountant",
                    cuenta: "Account",
                    actual: "Actual",
                    operacion: "Operation",
                    formulacion: "Formulation",
                    confirmDelete: "Confirm Delete",
                    areYouSure: "Are you sure?",
                    confirmDeleteText: "Are you sure you want to delete this record?",
                    cancel: "Cancel",
                    confirm: "Confirm"
                }
            }
        },

        selectedLevel1Data() {
            if (!this.filtroNivel1) return null
            return this.level1List.find(item => item.nivel_1 === this.filtroNivel1) || null
        },
        
        selectedLevel2Data() {
            if (!this.filtroNivel2) return null
            return this.level2List.find(item => item.nivel_2 === this.filtroNivel2) || null
        },
        
        selectedLevel3Data() {
            if (!this.filtroNivel3 || this.filtroNivel3 === 'null') return null
            return this.level3List.find(item => item.nivel_3 === this.filtroNivel3) || null
        },
        
        isFormValid() {
            return this.codReporte && this.selVigencia && this.companyId
        },
        
        isVigenciaBloqueada() {
            return this.selVigencia ? !this.$store.getters.isPeriodoEnabled(this.selVigencia) : false
        },
        
        hasNivel4Selected() {
            return this.filtroNivel4 !== '' && this.filtroNivel4 !== null && this.filtroNivel4 !== undefined
        },
        
        mostrarNivel1Table() {
            return this.filtroNivel1 === '' && this.filtroNivel2 === '' && this.filtroNivel3 === '' && this.filtroNivel4 === ''
        },
        
        mostrarNivel2Table() {
            return this.filtroNivel1 !== '' && this.filtroNivel2 === '' && this.filtroNivel3 === '' && this.filtroNivel4 === ''
        },
        
        mostrarNivel3Table() {
            return this.filtroNivel1 !== '' && this.filtroNivel2 !== '' && this.filtroNivel3 === '' && this.filtroNivel4 === ''
        },
        
        mostrarNivel4Table() {
            // Ahora nivel 4 siempre se muestra cuando se selecciona nivel 2
            return this.filtroNivel1 !== '' && this.filtroNivel2 !== '' && this.filtroNivel4 === ''
        },
        
        level1TableData() {
            return this.level1List.map(item => ({
                id: item.id,
                codigo: item.nivel_1,
                descripcion: item.descripcion
            }))
        },
        
        level2TableData() {
            return this.level2List.map(item => {
                const padre = this.level1List.find(l1 => l1.nivel_1 === item.nivel_1)
                return {
                    id: item.id,
                    codigo: item.nivel_2,
                    descripcion: item.descripcion,
                    perteneceA: padre ? `${padre.nivel_1} - ${padre.descripcion}` : item.nivel_1
                }
            })
        },
        
        level3TableData() {
            return this.level3List
                .filter(item => item.nivel_3 !== null)
                .map(item => {
                    const padre = this.level2List.find(l2 => l2.nivel_2 === item.nivel_2)
                    return {
                        id: item.id,
                        codigo: item.nivel_3,
                        descripcion: item.descripcion,
                        perteneceA: padre ? `${padre.nivel_2} - ${padre.descripcion}` : item.nivel_2
                    }
                })
        },
        
        level4TableData() {
            return this.level4List.map(item => {
                const padre = this.level3List.find(l3 => l3.nivel_3 === item.nivel_3)
                return {
                    id: item.id,
                    codigo: item.nivel_4,
                    descripcion: item.descripcion,
                    perteneceA: padre ? `${padre.nivel_3} - ${padre.descripcion}` : item.nivel_3
                }
            })
        },
        
        nivel1Fields() {
            return [
                { key: 'codigo', label: this.labels.codigo, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'descripcion', label: this.labels.descripcion, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'accionEdit', label: this.labels.edit, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false },
                { key: 'accionDelete', label: this.labels.delete, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false }
            ]
        },
        
        nivel2Fields() {
            return [
                { key: 'codigo', label: this.labels.codigo, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'descripcion', label: this.labels.descripcion, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'perteneceA', label: this.labels.perteneceA, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'accionEdit', label: this.labels.edit, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false },
                { key: 'accionDelete', label: this.labels.delete, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false }
            ]
        },
        
        nivel3Fields() {
            return [
                { key: 'codigo', label: this.labels.codigo, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'descripcion', label: this.labels.descripcion, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'perteneceA', label: this.labels.perteneceA, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'accionEdit', label: this.labels.edit, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false },
                { key: 'accionDelete', label: this.labels.delete, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false }
            ]
        },
        
        nivel4Fields() {
            return [
                { key: 'codigo', label: this.labels.codigo, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'descripcion', label: this.labels.descripcion, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'perteneceA', label: this.labels.perteneceA, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'accionEdit', label: this.labels.edit, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false },
                { key: 'accionDelete', label: this.labels.delete, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false }
            ]
        },
        
        nivel1FilterDefs() {
            return [
                { key: 'codigo', type: 'text', placeholder: 'Filtrar código...' },
                { key: 'descripcion', type: 'text', placeholder: 'Filtrar descripción...' }
            ]
        },
        
        nivel2FilterDefs() {
            return [
                { key: 'codigo', type: 'text', placeholder: 'Filtrar código...' },
                { key: 'descripcion', type: 'text', placeholder: 'Filtrar descripción...' },
                { key: 'perteneceA', type: 'text', placeholder: 'Filtrar pertenece a...' }
            ]
        },
        
        nivel3FilterDefs() {
            return [
                { key: 'codigo', type: 'text', placeholder: 'Filtrar código...' },
                { key: 'descripcion', type: 'text', placeholder: 'Filtrar descripción...' },
                { key: 'perteneceA', type: 'text', placeholder: 'Filtrar pertenece a...' }
            ]
        },
        
        nivel4FilterDefs() {
            return [
                { key: 'codigo', type: 'text', placeholder: 'Filtrar código...' },
                { key: 'descripcion', type: 'text', placeholder: 'Filtrar descripción...' },
                { key: 'perteneceA', type: 'text', placeholder: 'Filtrar pertenece a...' }
            ]
        },
        
        nivel1TableLabels() {
            return {
                noRow: this.labels.noLevel1Data,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        },
        
        nivel2TableLabels() {
            return {
                noRow: this.labels.noLevel2Data,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        },
        
        nivel3TableLabels() {
            return {
                noRow: this.labels.noLevel3Data,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        },
        
        nivel4TableLabels() {
            return {
                noRow: this.labels.noLevel4Data,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        },
        
        level3ListFiltrados() {
            return this.level3List.filter(item => item.nivel_3 !== null)
        },
        
        isCreateFormValid() {
            const lvl = this.createData.selectedLevel
            if (lvl == 1) {
                return this.createData.nivel1.codigo && this.createData.nivel1.descripcion
            } else if (lvl == 2) {
                return this.createData.nivel2.parentId && this.createData.nivel2.codigo && this.createData.nivel2.descripcion
            } else if (lvl == 3) {
                return this.createData.nivel3.parentId && this.createData.nivel3.codigo && this.createData.nivel3.descripcion
            } else if (lvl == 4) {
                return this.createData.nivel4.parentId && this.createData.nivel4.codigo && this.createData.nivel4.descripcion
            }
            return false
        },
        
        detailFields() {
            return [
                { key: 'contable', label: this.labels.contable, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'cuenta', label: this.labels.cuenta, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'actual', label: this.labels.actual, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'operacion', label: this.labels.operacion, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'formulacion', label: this.labels.formulacion, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'accionEdit', label: this.labels.edit, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false },
                { key: 'accionDelete', label: this.labels.delete, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false }
            ]
        },
        
        detailFilterDefs() {
            return [
                { key: 'cuenta', type: 'text', placeholder: 'Filtrar cuenta...' },
                { key: 'operacion', type: 'text', placeholder: 'Filtrar operación...' },
                { key: 'formulacion', type: 'text', placeholder: 'Filtrar formulación...' }
            ]
        },
        
        detailTableLabels() {
            return {
                noRow: this.labels.noAccounts,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        }
    },
    watch: {
        selVigencia(valNew) {
            if (valNew !== this.vigencia_anterior) {
                this.vigencia_anterior = valNew
                if (this.isVigenciaBloqueada) {
                    this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                }
            }
        }
    },
    methods: {
        goBack() {
            this.$router.back()
        },

        toggleFilters() {
            this.showFilters = !this.showFilters
        },

        getLevelClass(nivel) {
            const map = {
                'Nivel 4': 'level-4-text'
            }
            return map[nivel] || ''
        },

        f_filterCompany(valor = null) {
            console.log('=== FILTRO DE COMPAÑÍAS (Niveles) ===');
            console.log('valor recibido por @change:', valor);
            console.log('this.grupoId:', this.grupoId);

            const grupoIdSeleccionado = valor !== null && valor !== undefined
                ? valor
                : this.grupoId;

            console.log('grupoIdSeleccionado:', grupoIdSeleccionado);

            if (grupoIdSeleccionado) {
                const grupoIdStr = String(grupoIdSeleccionado).trim();

                this.filteredCompanies = this.listCompany.filter(company => {
                    if (!company?.id_grupos) return false;
                    const gruposCompania = String(company.id_grupos)
                        .split(',')
                        .map(id => id.trim());
                    return gruposCompania.includes(grupoIdStr);
                });

                console.log('Compañías filtradas:', this.filteredCompanies.map(c => c.nombre));
            } else {
                console.log('Mostrando TODAS las compañías');
                this.filteredCompanies = this.listCompany;
            }

            this.companyId = "";
            this.resetNiveles();
            console.log('=== FIN FILTRO ===');
        },

        f_actualiza_compania() {
            console.log('Compañía seleccionada:', this.companyId);
            if (this.companyId) {
                this.resetNiveles();
                this.compania_anterior = this.companyId;
            }
        },

        f_actualiza_reporte() {
            if (this.codReporteAnterior != this.codReporte) {
                this.resetNiveles()
                this.codReporteAnterior = this.codReporte
            }
        },

        f_actualiza_vigencia() {
            if (this.vigencia_anterior != this.selVigencia) {
                this.resetNiveles()
                this.vigencia_anterior = this.selVigencia
            }
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
            }
        },

        resetNiveles() {
            this.showDos = false
            this.showTres = false
            this.showCuatro = false
            this.level1List = []
            this.level2List = []
            this.level3List = []
            this.level4List = []
            this.filtroNivel1 = ''
            this.filtroNivel2 = ''
            this.filtroNivel3 = ''
            this.filtroNivel4 = ''
            this.hasNullNivel3 = false
            this.detalleNivCuatro = []
            this.nivelSelect = {}
            this.dataVigenCompa = {}
            this.consultaRealizada = false
            this.showNiveles = false
        },

        consultaNiveles() {
            if (this.selVigencia != "" && this.companyId != "" && this.codReporte != "") {
                this.showNiveles = true
                this.showFilters = false
                this.consultaRealizada = true
                this.dataVigenCompa = {
                    vigencia: this.selVigencia,
                    compania: this.companyId,
                    codReporte: this.codReporte
                }
                this.cargarNivel1()
            } else {
                this.$toast.error("Diligenciar los campos: reporte, vigencia y compañía")
            }
        },

        onNivel1Change(valor) {
            if (!valor) {
                this.filtroNivel2 = ''
                this.filtroNivel3 = ''
                this.filtroNivel4 = ''
                this.level2List = []
                this.level3List = []
                this.level4List = []
                this.detalleNivCuatro = []
                this.showDos = false
                this.showTres = false
                this.showCuatro = false
                this.hasNullNivel3 = false
                return
            }
            
            this.filtroNivel2 = ''
            this.filtroNivel3 = ''
            this.filtroNivel4 = ''
            this.level2List = []
            this.level3List = []
            this.level4List = []
            this.detalleNivCuatro = []
            this.showDos = false
            this.showTres = false
            this.showCuatro = false
            this.hasNullNivel3 = false

            if (valor) {
                this.cargarNivel2(valor)
            }
        },

        onNivel2Change(valorN2) {
            const valorN1 = this.filtroNivel1
            
            if (!valorN2) {
                this.filtroNivel2 = ''
                this.filtroNivel3 = ''
                this.filtroNivel4 = ''
                this.level3List = []
                this.level4List = []
                this.detalleNivCuatro = []
                this.showTres = false
                this.showCuatro = false
                this.hasNullNivel3 = false
                return
            }
            
            // Limpiar filtros inferiores
            this.filtroNivel3 = ''
            this.filtroNivel4 = ''
            this.level3List = []
            this.level4List = []
            this.detalleNivCuatro = []
            this.showTres = false
            this.showCuatro = false
            this.hasNullNivel3 = false

            if (valorN1 && valorN2) {
                // 1. Cargar nivel 3 para verificar si existe o es null
                this.cargarNivel3ParaAutoSelect(valorN1, valorN2)
            }
        },

        cargarNivel3ParaAutoSelect(nivUno, nivDos) {
            this.isLoading = true
            const isDirecto = this.$route.params.tipo == 'directo'
            const service = isDirecto ? Nivel.getLevThreeByLevOneLevTwo : Nivel.getLevThreeByLevOneLevTwoInd
            
            service(nivUno, nivDos)
                .then((res) => {
                    this.level3List = res.data.nivelTresEfe || []
                    this.showTres = true
                    
                    // Verificar si hay valores de nivel 3
                    const tieneValor = this.level3List.some(item => item.nivel_3 !== null)
                    const tieneNull = this.level3List.some(item => item.nivel_3 === null)
                    
                    // Si existe null, seleccionarlo automáticamente
                    if (tieneNull) {
                        // Si existe la opción null, seleccionarla automáticamente
                        this.hasNullNivel3 = true
                        this.filtroNivel3 = 'null'  // Seleccionar null automáticamente
                        
                        // Disparar la carga del nivel 4 con null
                        this.cargarNivel4ConNull(nivUno, nivDos)
                    } else {
                        // No hay null, solo valores de nivel 3
                        this.hasNullNivel3 = false
                        this.filtroNivel3 = ''
                        this.showCuatro = false
                        this.level4List = []
                        
                        if (tieneValor) {
                            // Tiene valores de nivel 3 pero no null
                            this.$toast.warning('No hay nivel 3 con valor null. Debe crearlo para continuar.', {
                                position: 'top-right',
                                duration: 5000
                            })
                        } else {
                            // No hay ningún nivel 3
                            this.$toast.warning('No hay niveles 3 disponibles. Debe crearlos para continuar.', {
                                position: 'top-right',
                                duration: 5000
                            })
                        }
                    }
                })
                .catch((err) => {
                    console.error(err)
                    this.$toast.error('Error al cargar nivel 3')
                    this.hasNullNivel3 = false
                    this.showCuatro = false
                })
                .finally(() => {
                    this.isLoading = false
                })
        },

        cargarNivel4ConNull(nivUno, nivDos) {
            console.log('=== Cargando nivel 4 con null ===');
            this.isLoading = true
            const isDirecto = this.$route.params.tipo == 'directo'
            
            // Usar el servicio que espera null
            const service = isDirecto 
                ? Nivel.getLevFourByLevOneLevTwoLevThreeNull 
                : Nivel.getLevFourByLevOneLevTwoLevThreeNullInd
            
            service(nivUno, nivDos, null)
                .then((res) => {
                    this.level4List = res.data.nivelCuatroEfe || []
                    this.showCuatro = true
                    
                    console.log('Nivel 4 cargado:', this.level4List.length);
                    
                    // Si hay solo un nivel 4, seleccionarlo automáticamente
                    if (this.level4List.length === 1) {
                        const unicoNivel4 = this.level4List[0].nivel_4
                        this.filtroNivel4 = unicoNivel4
                        // Disparar la carga del detalle
                        this.onNivel4Change(unicoNivel4)
                    } else if (this.level4List.length > 1) {
                        // Si hay varios, dejar que el usuario elija
                        this.filtroNivel4 = ''
                        this.detalleNivCuatro = []
                    } else {
                        // No hay nivel 4
                        this.showCuatro = false
                        this.detalleNivCuatro = []
                        this.$toast.warning('No hay nivel 4 disponible con nivel 3 null')
                    }
                })
                .catch((err) => {
                    console.error(err)
                    this.$toast.error('Error al cargar nivel 4')
                    this.level4List = []
                    this.showCuatro = false
                })
                .finally(() => {
                    this.isLoading = false
                })
        },

        onNivel3Change() {
            const valorN1 = this.filtroNivel1
            const valorN2 = this.filtroNivel2
            const valorN3 = this.filtroNivel3
            
            this.filtroNivel4 = ''
            this.level4List = []
            this.detalleNivCuatro = []
            this.showCuatro = false
            
            if (!valorN1 || !valorN2) {
                return
            }
            
            // Si selecciona 'null', cargar nivel 4 con null
            if (valorN3 === 'null') {
                console.log('Se seleccionó NULL, cargando nivel 4 con null');
                this.cargarNivel4(valorN1, valorN2, null)
                return
            }
            
            // Si tiene un valor, solo mostrar que está seleccionado
            if (valorN3 && valorN3 !== 'null') {
                console.log('Nivel 3 seleccionado con valor:', valorN3);
                this.showCuatro = false
                this.level4List = []
                return
            }
            
            // Si no hay selección
            this.showCuatro = false
            this.level4List = []
        },

        onNivel4Change(valorN4) {
            const valorN1 = this.filtroNivel1
            const valorN2 = this.filtroNivel2
            // IMPORTANTE: Si filtroNivel3 es 'null' o cadena vacía, debe ser null
            const valorN3 = this.filtroNivel3 === 'null' || this.filtroNivel3 === '' 
                ? null 
                : this.filtroNivel3

            this.filtroNivel4 = valorN4

            if (
                valorN1 &&
                valorN2 &&
                valorN4 !== '' &&
                valorN4 !== null &&
                valorN4 !== undefined
            ) {
                this.nivelSelect = {
                    nivelUno: valorN1,
                    nivelDos: valorN2,
                    nivelTres: valorN3,  // Asegurar que sea null si es 'null' o vacío
                    nivelCuatro: valorN4
                }

                this.cargarDetalleNivel4(
                    valorN1,
                    valorN2,
                    valorN3,  // Pasar null o el valor
                    valorN4
                )
            } else {
                this.detalleNivCuatro = []
            }
        },

        cargarNivel1() {
            this.isLoading = true
            const isDirecto = this.$route.params.tipo == 'directo'
            const service = isDirecto ? Nivel.getAllNivelUno : Nivel.getAllNivelUnoInd
            
            service()
                .then((res) => {
                    this.level1List = res.data.nivelUnoEfe || []
                })
                .catch((err) => {
                    console.error(err)
                    this.$toast.error('Error al cargar nivel 1')
                })
                .finally(() => {
                    this.isLoading = false
                })
        },

        cargarNivel2(nivUno) {
            this.isLoading = true
            const isDirecto = this.$route.params.tipo == 'directo'
            const service = isDirecto ? Nivel.getLevTwoByLevOne : Nivel.getLevTwoByLevOneInd

            service(nivUno)
                .then((res) => {
                    this.level2List = res.data.nivelDosEfe || []
                    this.showDos = true
                })
                .catch((err) => {
                    console.error(err)
                    this.$toast.error('Error al cargar nivel 2')
                })
                .finally(() => {
                    this.isLoading = false
                })
        },

        cargarNivel3(nivUno, nivDos) {
            this.isLoading = true
            const isDirecto = this.$route.params.tipo == 'directo'
            const service = isDirecto ? Nivel.getLevThreeByLevOneLevTwo : Nivel.getLevThreeByLevOneLevTwoInd
            
            service(nivUno, nivDos)
                .then((res) => {
                    this.level3List = res.data.nivelTresEfe || []
                    this.showTres = true
                    
                    const tieneNull = this.level3List.some(item => item.nivel_3 === null)
                    const tieneValor = this.level3List.some(item => item.nivel_3 !== null)
                    
                    if (tieneNull && !tieneValor) {
                        this.hasNullNivel3 = true
                        this.filtroNivel3 = ''
                        this.showCuatro = false
                        this.level4List = []
                    } else if (tieneNull && tieneValor) {
                        this.hasNullNivel3 = true
                        this.filtroNivel3 = ''
                        this.showCuatro = false
                        this.level4List = []
                    } else {
                        this.hasNullNivel3 = false
                        this.filtroNivel3 = ''
                        this.showCuatro = false
                        this.level4List = []
                    }
                })
                .catch((err) => {
                    console.error(err)
                    this.$toast.error('Error al cargar nivel 3')
                    this.hasNullNivel3 = false
                })
                .finally(() => {
                    this.isLoading = false
                })
        },

        cargarNivel4(nivUno, nivDos, nivTres) {
            this.isLoading = true
            const isDirecto = this.$route.params.tipo == 'directo'
            
            let service, params
            if (nivTres === null || nivTres === 'null') {
                service = isDirecto ? Nivel.getLevFourByLevOneLevTwoLevThreeNull : Nivel.getLevFourByLevOneLevTwoLevThreeNullInd
                params = [nivUno, nivDos, null]
            } else {
                service = isDirecto ? Nivel.getLevFourByLevOneLevTwoLevThree : Nivel.getLevFourByLevOneLevTwoLevThreeInd
                params = [nivUno, nivDos, nivTres]
            }
            
            service(...params)
                .then((res) => {
                    this.level4List = res.data.nivelCuatroEfe || []
                    this.showCuatro = true
                })
                .catch((err) => {
                    console.error(err)
                    this.$toast.error('Error al cargar nivel 4')
                    this.level4List = []
                    this.showCuatro = false
                })
                .finally(() => {
                    this.isLoading = false
                })
        },

        cargarDetalleNivel4(nivUno, nivDos, nivTres, nivCuatro) {
            this.isLoading = true
            const isDirecto = this.$route.params.tipo == 'directo'
            
            // Normalizar nivTres: si es 'null' o vacío, convertirlo a null
            const nivTresNormalizado = (nivTres === 'null' || nivTres === '' || nivTres === null) 
                ? null 
                : nivTres
            
            console.log('=== cargarDetalleNivel4 ===');
            console.log('nivTres original:', nivTres);
            console.log('nivTres normalizado:', nivTresNormalizado);
            
            let service
            // Usar el valor normalizado para decidir
            if (nivTresNormalizado === null) {
                service = isDirecto 
                    ? Nivel.getLevDetalFourByLevOneLevTwoLevThreeNullLevFour 
                    : Nivel.getLevDetalFourByLevOneLevTwoLevThreeNullLevFourInd
                service(nivUno, nivDos, null, nivCuatro, this.selVigencia, this.companyId, this.codReporte)
                    .then((res) => {
                        this.detalleNivCuatro = res.data.nivCuatroEfeDet || []
                        console.log('Detalle cargado (con null):', this.detalleNivCuatro.length);
                    })
                    .catch((err) => {
                        if (err.response?.status == 404) {
                            this.detalleNivCuatro = []
                            console.log('No hay detalle para este nivel 4 (con null)');
                        } else {
                            console.error('Error al cargar el detalle:', err);
                            this.$toast.error('Error al cargar el detalle')
                        }
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            } else {
                service = isDirecto 
                    ? Nivel.getLevDetalFourByLevOneLevTwoLevThreeLevFour 
                    : Nivel.getLevDetalFourByLevOneLevTwoLevThreeLevFourInd
                service(nivUno, nivDos, nivTresNormalizado, nivCuatro, this.selVigencia, this.companyId, this.codReporte)
                    .then((res) => {
                        this.detalleNivCuatro = res.data.nivCuatroEfeDet || []
                        console.log('Detalle cargado (con valor):', this.detalleNivCuatro.length);
                    })
                    .catch((err) => {
                        if (err.response?.status == 404) {
                            this.detalleNivCuatro = []
                            console.log('No hay detalle para este nivel 4 (con valor)');
                        } else {
                            console.error('Error al cargar el detalle:', err);
                            this.$toast.error('Error al cargar el detalle')
                        }
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            }
        },

        // Métodos para Nivel 1
        openCreateNivel1() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }
            this.showCreaNivUno = true
        },

        editNivel1() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }
            this.$toast.info('Acción restringida: Acceso administrador')
        },

        deleteNivel1() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar.')
                return
            }
            this.$toast.info('Acción restringida: Acceso administrador')
        },

        borrarTodosNiveles1() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar.')
                return
            }
            this.deleteId = null
            this.deleteTarget = 'nivel1'
            this.isDeleteAll = true
            this.showConfirmDeleteModal = true
        },

        onRefresNivelUno() {
            this.cargarNivel1()
        },

        // Métodos para Nivel 2
        openCreateNivel2() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }
            this.showCreaNivDos = true
        },

        editNivel2() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }
            this.$toast.info('Acción restringida: Acceso administrador')
        },

        deleteNivel2() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar.')
                return
            }
            this.$toast.info('Acción restringida: Acceso administrador')
        },

        borrarTodosNiveles2() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar.')
                return
            }
            this.deleteId = null
            this.deleteTarget = 'nivel2'
            this.isDeleteAll = true
            this.showConfirmDeleteModal = true
        },

        onRefresNivelDos() {
            this.cargarNivel2(this.filtroNivel1)
        },

        // Métodos para Nivel 3
        openCreateNivel3() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }
            this.showCreaNivTres = true
        },

        editNivel3() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }
            this.$toast.info('Acción restringida: Acceso administrador')
        },

        deleteNivel3() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar.')
                return
            }
            this.$toast.info('Acción restringida: Acceso administrador')
        },

        borrarTodosNiveles3() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar.')
                return
            }
            this.deleteId = null
            this.deleteTarget = 'nivel3'
            this.isDeleteAll = true
            this.showConfirmDeleteModal = true
        },

        onRefresNivelTres() {
            this.cargarNivel3(this.filtroNivel1, this.filtroNivel2)
        },

        // Métodos para Nivel 4
        openCreateNivel4() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }
            this.showCreaNivCuatro = true
        },

        editNivel4() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }
            this.$toast.info('Acción restringida: Acceso administrador')
        },

        deleteNivel4() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar.')
                return
            }
            this.$toast.info('Acción restringida: Acceso administrador')
        },

        borrarTodosNiveles4() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar.')
                return
            }
            this.deleteId = null
            this.deleteTarget = 'nivel4'
            this.isDeleteAll = true
            this.showConfirmDeleteModal = true
        },

        onRefresNivelCuatro() {
            const nivel3 = this.filtroNivel3 === 'null' ? null : this.filtroNivel3
            this.cargarNivel4(this.filtroNivel1, this.filtroNivel2, nivel3)
        },

        // Métodos para Cuentas
        openCreateCuentaModal() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }
            
            // Verificar que tengamos los datos necesarios
            if (!this.nivelSelect || !this.nivelSelect.nivelCuatro) {
                this.$toast.error('Debe seleccionar un nivel 4 para agregar cuentas.')
                return
            }
            
            // Construir el objeto nivelSelect con los datos actuales
            this.nivelSelect = {
                nivelUno: this.filtroNivel1,
                nivelDos: this.filtroNivel2,
                nivelTres: this.filtroNivel3 === 'null' ? null : this.filtroNivel3,
                nivelCuatro: this.filtroNivel4
            }
            
            // Construir el objeto dataVigenCompa
            this.dataVigenCompa = {
                vigencia: this.selVigencia,
                compania: this.companyId,
                codReporte: this.codReporte
            }
            
            // Obtener lista de cuentas ya creadas para pasarlas al modal
            this.cuentasCreadasList = this.detalleNivCuatro.map(item => item.cuenta)
            
            // Abrir el modal
            this.showCreaCuenta = true
        },


        closeCreaCuentaModal() {
            this.showCreaCuenta = false
        },

        reloadTableAfterCreate(nivelSelect) {
            // Recargar el detalle después de crear una cuenta
            this.cargarDetalleNivel4(
                nivelSelect.nivelUno,
                nivelSelect.nivelDos,
                nivelSelect.nivelTres,
                nivelSelect.nivelCuatro
            )
        },

        editCuenta(id) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }
            
            // Buscar la cuenta en el detalle actual
            const cuenta = this.detalleNivCuatro.find(item => item.id === id)
            if (!cuenta) {
                this.$toast.error('No se encontró la cuenta')
                return
            }
            
            // Guardar la cuenta seleccionada como un array (como espera EditaCuenta)
            this.cuentaSeleccionada = [cuenta]
            
            // Abrir el modal de edición
            this.showEditaCuenta = true
        },

        closeEditaCuentaModal() {
            this.showEditaCuenta = false
            this.cuentaSeleccionada = null
        },

        reloadTableAfterEdit(nivelSelect) {
            // Recargar el detalle después de editar una cuenta
            this.cargarDetalleNivel4(
                nivelSelect.nivelUno,
                nivelSelect.nivelDos,
                nivelSelect.nivelTres,
                nivelSelect.nivelCuatro
            )
        },

        deleteCuenta(id) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar.')
                return
            }
            this.deleteId = id
            this.deleteTarget = 'cuenta'
            this.isDeleteAll = false
            this.showConfirmDeleteModal = true
        },

        borrarTodosDetalles() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar.')
                return
            }
            this.deleteId = null
            this.deleteTarget = 'cuenta'
            this.isDeleteAll = true
            this.showConfirmDeleteModal = true
        },

        closeConfirmDeleteModal() {
            this.showConfirmDeleteModal = false
            this.deleteId = null
            this.isDeleteAll = false
            this.deleteTarget = 'cuenta'
        },

        executeDelete() {
            const isDirecto = this.$route.params.tipo == 'directo'
            
            if (this.deleteTarget === 'nivel1') {
                if (this.isDeleteAll) {
                    const dataForm = { codReporte: this.codReporte }
                    const service = isDirecto ? Nivel.deleteAllNivelUnoEfe : Nivel.deleteAllNivelUnoEfeInd
                    service(dataForm)
                        .then(() => {
                            this.$toast.success('Todos los niveles 1 eliminados.')
                            this.showConfirmDeleteModal = false
                            this.cargarNivel1()
                        })
                        .catch((err) => {
                            console.error(err)
                            this.$toast.error('Error al eliminar los niveles')
                        })
                } else {
                    const service = isDirecto ? Nivel.deleteNivelUnoEfe : Nivel.deleteNivelUnoEfeInd
                    service(this.deleteId)
                        .then(() => {
                            this.$toast.success('Nivel eliminado.')
                            this.showConfirmDeleteModal = false
                            this.cargarNivel1()
                        })
                        .catch((err) => {
                            console.error(err)
                            this.$toast.error('Error al eliminar el nivel')
                        })
                }
                return
            }
            
            if (this.deleteTarget === 'nivel2') {
                if (this.isDeleteAll) {
                    const dataForm = {
                        codReporte: this.codReporte,
                        nivel1: this.filtroNivel1
                    }
                    const service = isDirecto ? Nivel.deleteAllNivelDosEfe : Nivel.deleteAllNivelDosEfeInd
                    service(dataForm)
                        .then(() => {
                            this.$toast.success('Todos los niveles 2 eliminados.')
                            this.showConfirmDeleteModal = false
                            this.cargarNivel2(this.filtroNivel1)
                        })
                        .catch((err) => {
                            console.error(err)
                            this.$toast.error('Error al eliminar los niveles')
                        })
                } else {
                    const service = isDirecto ? Nivel.deleteNivelDosEfe : Nivel.deleteNivelDosEfeInd
                    service(this.deleteId)
                        .then(() => {
                            this.$toast.success('Nivel eliminado.')
                            this.showConfirmDeleteModal = false
                            this.cargarNivel2(this.filtroNivel1)
                        })
                        .catch((err) => {
                            console.error(err)
                            this.$toast.error('Error al eliminar el nivel')
                        })
                }
                return
            }
            
            if (this.deleteTarget === 'nivel3') {
                if (this.isDeleteAll) {
                    const dataForm = {
                        codReporte: this.codReporte,
                        nivel1: this.filtroNivel1,
                        nivel2: this.filtroNivel2
                    }
                    const service = isDirecto ? Nivel.deleteAllNivelTresEfe : Nivel.deleteAllNivelTresEfeInd
                    service(dataForm)
                        .then(() => {
                            this.$toast.success('Todos los niveles 3 eliminados.')
                            this.showConfirmDeleteModal = false
                            this.cargarNivel3(this.filtroNivel1, this.filtroNivel2)
                        })
                        .catch((err) => {
                            console.error(err)
                            this.$toast.error('Error al eliminar los niveles')
                        })
                } else {
                    const service = isDirecto ? Nivel.deleteNivelTresEfe : Nivel.deleteNivelTresEfeInd
                    service(this.deleteId)
                        .then(() => {
                            this.$toast.success('Nivel eliminado.')
                            this.showConfirmDeleteModal = false
                            this.cargarNivel3(this.filtroNivel1, this.filtroNivel2)
                        })
                        .catch((err) => {
                            console.error(err)
                            this.$toast.error('Error al eliminar el nivel')
                        })
                }
                return
            }
            
            if (this.deleteTarget === 'nivel4') {
                if (this.isDeleteAll) {
                    const dataForm = {
                        codReporte: this.codReporte,
                        nivel1: this.filtroNivel1,
                        nivel2: this.filtroNivel2,
                        nivel3: this.filtroNivel3 === 'null' ? null : this.filtroNivel3
                    }
                    const service = isDirecto ? Nivel.deleteAllNivelCuatroEfe : Nivel.deleteAllNivelCuatroEfeInd
                    service(dataForm)
                        .then(() => {
                            this.$toast.success('Todos los niveles 4 eliminados.')
                            this.showConfirmDeleteModal = false
                            const nivel3 = this.filtroNivel3 === 'null' ? null : this.filtroNivel3
                            this.cargarNivel4(this.filtroNivel1, this.filtroNivel2, nivel3)
                        })
                        .catch((err) => {
                            console.error(err)
                            this.$toast.error('Error al eliminar los niveles')
                        })
                } else {
                    const service = isDirecto ? Nivel.deleteNivelCuatroEfe : Nivel.deleteNivelCuatroEfeInd
                    service(this.deleteId)
                        .then(() => {
                            this.$toast.success('Nivel eliminado.')
                            this.showConfirmDeleteModal = false
                            const nivel3 = this.filtroNivel3 === 'null' ? null : this.filtroNivel3
                            this.cargarNivel4(this.filtroNivel1, this.filtroNivel2, nivel3)
                        })
                        .catch((err) => {
                            console.error(err)
                            this.$toast.error('Error al eliminar el nivel')
                        })
                }
                return
            }
            
            // Eliminar Cuentas
            if (this.isDeleteAll) {
                const dataForm = {
                    ...this.nivelSelect,
                    id_compania: this.companyId,
                    vigencia: this.selVigencia,
                    codReporte: this.codReporte
                }
                const service = isDirecto ? Nivel.deleteCuentaNivelEfeAll : Nivel.deleteCuentaNivelEfeIndAll
                service(dataForm)
                    .then(() => {
                        this.$toast.success('Todos los registros eliminados.')
                        this.showConfirmDeleteModal = false
                        const valorN3 = this.filtroNivel3 === 'null' ? null : this.filtroNivel3
                        this.cargarDetalleNivel4(this.filtroNivel1, this.filtroNivel2, valorN3, this.filtroNivel4)
                    })
                    .catch((err) => {
                        console.error(err)
                        this.$toast.error('Error al eliminar los registros')
                    })
            } else {
                const service = isDirecto ? Nivel.deleteCuentaNivelEfe : Nivel.deleteCuentaNivelEfeInd
                service(this.deleteId)
                    .then(() => {
                        this.$toast.success('Registro eliminado.')
                        this.showConfirmDeleteModal = false
                        const valorN3 = this.filtroNivel3 === 'null' ? null : this.filtroNivel3
                        this.cargarDetalleNivel4(this.filtroNivel1, this.filtroNivel2, valorN3, this.filtroNivel4)
                    })
                    .catch((err) => {
                        console.error(err)
                        this.$toast.error('Error al eliminar el registro')
                    })
            }
        },

        cargarDatosIniciales() {
            this.isLoading = true
            Promise.all([
                Balance.leaCompaniaUsuario(),
                PlanCuentas.getVigenciaPlancuentas(),
                Company.getAllCompanies(),
                Balance.getReportes(),
                Grupo.getAllGrupos()
            ])
                .then(([companiaRes, vigenciaRes, companyRes, reportesRes, grupoRes]) => {
                    this.companiaUsuario = companiaRes.data.companiaUsuario || []
                    this.listVigencia = vigenciaRes.data.getVigencia || []
                    this.reportes = reportesRes.data.reportes || []
                    this.listGrupos = grupoRes.data.grupos || []

                    let v_companias = companyRes.data.companies || []
                    if (this.$store.state.userRole != 1) {
                        v_companias = v_companias.filter(element => {
                            return this.companiaUsuario.some(r_usuario =>
                                r_usuario.id_usuario == this.$store.state.userId &&
                                r_usuario.id_compania == element.id
                            )
                        })
                    }
                    this.listCompany = v_companias
                    this.filteredCompanies = [...v_companias]
                })
                .catch((err) => {
                    console.error(err)
                    this.$toast.error('Error al cargar los datos iniciales')
                })
                .finally(() => {
                    this.isLoading = false
                })
        }
    },
    mounted() {
        this.cargarDatosIniciales()
    }
}
</script>

<style src="../main.css"></style>

<style scoped>
.niif-header .filters-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 0 !important;
}

.niif-header .filter-select {
    min-width: 180px;
    max-width: 280px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
}

.niif-header .filter-select option {
    white-space: normal;
    word-break: break-word;
    padding: 4px 8px;
}

.niif-header .consult-btn {
    height: 38px;
    min-height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    background-color: #6c757d !important;
    color: white !important;
    border: 1px solid #6c757d !important;
    cursor: pointer;
    flex-shrink: 0;
}

.niif-header .consult-btn:hover,
.niif-header .consult-btn:focus,
.niif-header .consult-btn:active,
.niif-header .consult-btn:not(:disabled):hover,
.niif-header .consult-btn:not(:disabled):focus,
.niif-header .consult-btn:not(:disabled):active {
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
    box-shadow: none !important;
}

.niif-header .consult-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
}

.page-back-btn {
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
}

.back-icon {
    font-size: 1.75rem !important;
    width: 1.75rem !important;
    height: 1.75rem !important;
}

.param-title {
    font-size: 2rem !important;
    font-weight: 500;
}

.toggle-filters-btn {
    color: #2e507a;
    font-weight: 500;
    font-size: 14px;
    padding: 4px 12px;
    text-decoration: none;
    border: 1px solid #dee2e6;
    border-radius: 7px;
    background: white;
    height: 38px;
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
}

.toggle-filters-btn:hover {
    background: #f8f9fa;
    text-decoration: none;
    color: #1a3455;
}

.nivel-filter {
    min-width: 160px;
    max-width: 220px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
}

.nivel-filter:disabled {
    background-color: #e9ecef;
    opacity: 0.7;
}

.level-4-text { 
    color: #7d3c98; 
    font-weight: 600;
}

.empty-table-icon {
    font-size: 40px;
    color: #d0d0d0;
}

.niif-toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex: 0 0 auto;
}

.table-toolbar-add-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #28a745;
    color: white;
    border: 1px solid #28a745;
}

.table-toolbar-add-btn:hover:not(:disabled) {
    background-color: #218838;
    border-color: #1e7e34;
}

.table-toolbar-add-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.table-action-btn {
    border: none;
    border-radius: 4px;
    transition: all 0.2s;
}

.uniform-modal-body { padding: 0 10px; }
.uniform-form-row { margin-bottom: 12px; }
.uniform-label-col label { margin-bottom: 0; font-weight: 500; font-size: 14px; }
.uniform-label-col { padding-right: 10px; }
.form-input { width: 100%; height: 38px; font-size: 14px; }

.reset-modal-header {
    background-color: #2e507a;
    color: white;
    padding: 16px 20px;
    border-radius: 8px 8px 0 0;
}
.reset-modal-title { font-size: 18px; font-weight: 600; margin: 0; }
.reset-modal-body { padding: 24px 20px; background-color: #f8f9fa; }

.password-policy-box {
    background: white;
    border-radius: 8px;
    padding: 16px 20px;
    border-left: 4px solid #dc3545;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}
.password-policy-title { font-weight: 600; font-size: 15px; color: #dc3545; margin-bottom: 4px; }
.password-policy-subtitle { font-size: 13px; color: #6c757d; }

.reset-dialog-actions { padding-top: 8px; }
.reset-btn-primary {
    background-color: #2e507a;
    border-color: #2e507a;
    color: white;
    padding: 8px 24px;
    border-radius: 6px;
    font-weight: 500;
}
.reset-btn-primary:hover:not(:disabled) {
    background-color: #1e3a5f;
    border-color: #1a3455;
    color: white;
}
.reset-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.reset-btn-secondary {
    background-color: #e9ecef;
    border-color: #ced4da;
    color: #495057;
    padding: 8px 24px;
    border-radius: 6px;
    font-weight: 500;
}
.reset-btn-secondary:hover {
    background-color: #dde1e6;
    border-color: #b8c1ca;
}

@media (max-width: 1200px) {
    .niif-header .filter-select {
        min-width: 160px;
        max-width: 220px;
        height: 36px;
        font-size: 13px;
    }
    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
    .nivel-filter {
        min-width: 140px;
        max-width: 190px;
        height: 36px;
        font-size: 13px;
    }
    .toggle-filters-btn {
        font-size: 13px;
        height: 36px;
        padding: 4px 10px;
    }
}

@media (max-width: 992px) {
    .niif-header .filters-container {
        flex-wrap: wrap;
        width: 100%;
        margin-top: 8px;
        margin-left: 0 !important;
        justify-content: flex-start;
        gap: 6px;
    }
    .niif-header .filter-select {
        min-width: 150px;
        max-width: 200px;
        height: 36px;
        font-size: 13px;
        flex: 1 1 auto;
    }
    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
    .nivel-filter {
        min-width: 120px;
        max-width: 170px;
        height: 36px;
        font-size: 13px;
    }
    .toggle-filters-btn {
        font-size: 12px;
        height: 36px;
        padding: 4px 8px;
    }
}

@media (max-width: 576px) {
    .param-title {
        font-size: 1.5rem !important;
    }
    .niif-header .filter-select {
        min-width: 100%;
        max-width: 100%;
        height: 36px;
        font-size: 13px;
        flex: 0 0 100%;
    }
    .niif-header .filters-container {
        gap: 4px;
    }
    .niif-header .consult-btn {
        width: 100%;
        flex: 0 0 100%;
        height: 36px;
        margin-left: 0 !important;
        margin-top: 4px;
    }
    .nivel-filter {
        min-width: 100%;
        max-width: 100%;
        height: 36px;
        font-size: 13px;
        margin-left: 0 !important;
        margin-top: 4px;
    }
    .toggle-filters-btn {
        width: 100%;
        justify-content: center;
        height: 36px;
        margin-bottom: 4px;
        margin-right: 0 !important;
    }
    .uniform-label-col {
        flex: 0 0 100%;
        max-width: 100%;
        margin-bottom: 4px;
        text-align: left !important;
    }
    .uniform-form-row .col-md-8 {
        flex: 0 0 100%;
        max-width: 100%;
    }
}
</style>